import { supabase } from '../lib/supabase';
import { productionSMSService } from './productionSMSService';

export const urgentSMSService = {
  /**
   * Send urgent SMS alert to field team members
   * Delegates to productionSMSService for full Twilio-compliant delivery
   */
  async sendUrgentAlert(params) {
    try {
      const { alertType, severity, title, message, recipientUserIds, companyId } = params;

      if (!alertType || !severity || !title || !message) {
        throw new Error('Missing required alert fields');
      }

      // Resolve recipients
      let userIds = recipientUserIds || [];

      if ((!userIds || userIds?.length === 0) && companyId) {
        const { data: employees, error: empError } = await supabase
          ?.from('company_employees')
          ?.select('user_id')
          ?.eq('company_id', companyId)
          ?.eq('status', 'active')
          ?.in('department', ['Field Operations', 'IPM Team', 'Compliance']);

        if (empError) throw empError;
        userIds = (employees || [])?.map(e => e?.user_id);
      }

      if (userIds?.length === 0) {
        return { sent: false, reason: 'No recipients found' };
      }

      // Use production SMS service for compliant delivery
      const batchResult = await productionSMSService?.sendBatchAlerts(userIds, {
        alertType,
        severity,
        title,
        message
      });

      return {
        sent: batchResult?.sent > 0,
        totalRecipients: batchResult?.totalRecipients,
        successCount: batchResult?.sent,
        failedCount: batchResult?.failed,
        results: batchResult?.results
      };
    } catch (error) {
      console.error('Error sending urgent SMS alert:', error);
      throw error;
    }
  },

  /**
   * Send compliance violation SMS alert
   */
  async sendComplianceViolationAlert(violation) {
    return this.sendUrgentAlert({
      alertType: 'compliance_violation',
      severity: violation?.severity,
      title: violation?.title,
      message: violation?.message,
      companyId: violation?.companyId
    });
  },

  /**
   * Send treatment alert SMS
   */
  async sendTreatmentAlert(treatment) {
    return this.sendUrgentAlert({
      alertType: 'treatment_alert',
      severity: treatment?.severity,
      title: treatment?.title,
      message: treatment?.message,
      recipientUserIds: treatment?.recipientUserIds,
      companyId: treatment?.companyId
    });
  },

  /**
   * Test SMS functionality
   */
  async sendTestSMS(phoneNumber, message = 'This is a test message from Grow Guardian.') {
    try {
      const response = await fetch(
        `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-twilio-sms`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`
          },
          body: JSON.stringify({
            to: phoneNumber,
            message,
            severity: 'critical' // Test messages bypass priority filter
          })
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData.error || 'Failed to send test SMS');
      }

      const result = await response?.json();
      return { success: true, messageSid: result?.messageSid };
    } catch (error) {
      console.error('Error sending test SMS:', error);
      throw error;
    }
  },

  /**
   * Handle STOP command from user
   */
  async handleStopCommand(userId, phoneNumber) {
    return productionSMSService?.handleStopCommand(userId, phoneNumber);
  },

  /**
   * Handle START command from user
   */
  async handleStartCommand(userId, phoneNumber) {
    return productionSMSService?.handleStartCommand(userId, phoneNumber);
  },

  /**
   * Get SMS delivery stats
   */
  async getSMSStats(userId, dateRange = {}) {
    return productionSMSService?.getSMSStats(userId, dateRange);
  }
};